import { certificationApi } from '@/lib/certification-api';
import { 
  IProgressUseCase 
} from '../usecases/progress.usecase';
import { 
  ProgressRepository,
  GetLessonProgressData,
  GetQuizProgressData,
  GetModuleProgressData,
  GetCourseProgressData,
  QuizAnswerData
} from '../repository/progress.repository';
import { 
  IAnalyticsUseCase 
} from '../usecases/analytics.usecase';
import { CourseRepository } from '../repository/course.repository';
import { QuizRepository } from '../repository/quiz.repository';

/**
 * Progress Service
 * Handles all course progress tracking including lessons, quizzes, modules, and courses
 * Provides analytics and learning insights
 */
export class ProgressService {
  constructor(
    private progressUseCase: IProgressUseCase,
    private analyticsUseCase?: IAnalyticsUseCase
  ) {
    console.log('[ProgressService] Constructor - Initialized');
  }

  // ===== LESSON PROGRESS TRACKING =====

  /**
   * Start tracking progress for a lesson
   * Called when user first opens/accesses a lesson
   */
  async startLesson(userId: string, courseId: string, moduleId: string, lessonId: string): Promise<{
    success: boolean;
    progress?: GetLessonProgressData;
    error?: string;
  }> {
    console.log('[ProgressService.startLesson] START - User:', userId, 'Lesson:', lessonId);
    
    try {
      const progress = await this.progressUseCase.startLesson(userId, courseId, moduleId, lessonId);

      // Track analytics event
      if (this.analyticsUseCase) {
        try {
          await this.analyticsUseCase.trackActivity(
            userId,
            'lessonStarted',
            {
              courseId: courseId,
              moduleId: moduleId,
              lessonId: lessonId,
              progressId: progress.id
            }
          );
        } catch (analyticsError) {
          console.warn('[ProgressService.startLesson] Analytics tracking failed:', analyticsError);
        }
      }

      console.log('[ProgressService.startLesson] SUCCESS - Progress ID:', progress.id);
      return { success: true, progress };

    } catch (error) {
      console.error('[ProgressService.startLesson] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to start lesson progress' 
      };
    }
  }

  /**
   * Update lesson progress (video progress, time spent, etc.)
   * Called periodically during lesson consumption
   */
  async updateLessonProgress(userId: string, lessonId: string, progressData: {
    completionPercentage?: number;
    timeSpentSeconds?: number;
    videoProgressSeconds?: number;
    notes?: string;
    bookmarked?: boolean;
  }): Promise<{
    success: boolean;
    progress?: GetLessonProgressData;
    error?: string;
  }> {
    console.log('[ProgressService.updateLessonProgress] START - User:', userId, 'Lesson:', lessonId, 'Data:', progressData);
    
    try {
      const progress = await this.progressUseCase.updateLessonProgress(userId, lessonId, progressData);

      // Track significant progress milestones
      if (this.analyticsUseCase && progressData.completionPercentage) {
        const milestones = [25, 50, 75, 100];
        const milestone = milestones.find(m => 
          progressData.completionPercentage! >= m && 
          (progress.completionPercentage < m || progress.completionPercentage === progressData.completionPercentage)
        );

        if (milestone) {
          try {
            await this.analyticsUseCase.trackActivity(
              userId,
              'lessonProgressMilestone',
              {
                lessonId: lessonId,
                milestonePercentage: milestone,
                timeSpentSeconds: progressData.timeSpentSeconds || 0,
                progressId: progress.id
              }
            );
          } catch (analyticsError) {
            console.warn('[ProgressService.updateLessonProgress] Analytics tracking failed:', analyticsError);
          }
        }
      }

      console.log('[ProgressService.updateLessonProgress] SUCCESS - Progress updated');
      return { success: true, progress };

    } catch (error) {
      console.error('[ProgressService.updateLessonProgress] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to update lesson progress' 
      };
    }
  }

  /**
   * Mark lesson as completed
   * Called when user completes a lesson
   */
  async completeLesson(userId: string, lessonId: string, timeSpentSeconds: number): Promise<{
    success: boolean;
    progress?: GetLessonProgressData;
    moduleProgress?: GetModuleProgressData;
    courseProgress?: GetCourseProgressData;
    error?: string;
  }> {
    console.log('[ProgressService.completeLesson] START - User:', userId, 'Lesson:', lessonId, 'Time:', timeSpentSeconds);
    
    try {
      // The record that counts toward completion and the certificate is
      // written by the server (enrollments/{id}.lessonProgress). Do that
      // first; the client-side progress below is display state only.
      await certificationApi.markLessonCompleted(lessonId, undefined, timeSpentSeconds);

      const progress = await this.progressUseCase.completeLesson(userId, lessonId, timeSpentSeconds);

      // completeLesson already cascades into module and course progress.
      // Recalculating both again here ran that whole chain -- each pass
      // re-reading every progress record the learner has -- two more times,
      // and Mark as Complete took ~10s against a ~1s server call. Read the
      // results instead of recomputing them.
      const summary = await this.progressUseCase.getUserProgressSummary(userId, progress.courseId);
      const moduleProgress = summary.moduleProgress.find((m) => m.moduleId === progress.moduleId);
      const courseProgress = summary.courseProgress[0];

      // Track lesson completion analytics without holding up the learner.
      if (this.analyticsUseCase) {
        void (async () => {
        try {
          await this.analyticsUseCase!.trackActivity(
            userId,
            'lessonCompleted',
            {
              courseId: progress.courseId,
              moduleId: progress.moduleId,
              lessonId: lessonId,
              timeSpentSeconds: timeSpentSeconds,
              progressId: progress.id,
              moduleCompletionPercentage: moduleProgress?.completionPercentage,
              courseCompletionPercentage: courseProgress?.completionPercentage
            }
          );
        } catch (analyticsError) {
          console.warn('[ProgressService.completeLesson] Analytics tracking failed:', analyticsError);
        }
        })();
      }

      console.log('[ProgressService.completeLesson] SUCCESS - Lesson completed');
      return { 
        success: true, 
        progress, 
        moduleProgress, 
        courseProgress 
      };

    } catch (error) {
      console.error('[ProgressService.completeLesson] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to complete lesson' 
      };
    }
  }

  // ===== QUIZ PROGRESS TRACKING =====

  /**
   * Start a new quiz attempt
   * Called when user begins taking a quiz
   */
  async startQuiz(userId: string, courseId: string, quizId: string, moduleId?: string, lessonId?: string): Promise<{
    success: boolean;
    progress?: GetQuizProgressData;
    error?: string;
  }> {
    console.log('[ProgressService.startQuiz] START - User:', userId, 'Quiz:', quizId);
    
    try {
      const progress = await this.progressUseCase.startQuiz(userId, courseId, quizId, moduleId, lessonId);

      // Track quiz start analytics
      if (this.analyticsUseCase) {
        try {
          await this.analyticsUseCase.trackActivity(
            userId,
            'quizStarted',
            {
              courseId: courseId,
              moduleId: moduleId,
              lessonId: lessonId,
              quizId: quizId,
              attemptNumber: progress.attemptNumber,
              progressId: progress.id
            }
          );
        } catch (analyticsError) {
          console.warn('[ProgressService.startQuiz] Analytics tracking failed:', analyticsError);
        }
      }

      console.log('[ProgressService.startQuiz] SUCCESS - Quiz attempt started');
      return { success: true, progress };

    } catch (error) {
      console.error('[ProgressService.startQuiz] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to start quiz' 
      };
    }
  }

  /**
   * Submit an answer to a quiz question
   * Called when user answers a question during quiz
   */
  async submitQuizAnswer(userId: string, quizId: string, questionId: string, answer: string | string[], timeSpentSeconds: number): Promise<{
    success: boolean;
    progress?: GetQuizProgressData;
    isCorrect?: boolean;
    error?: string;
  }> {
    console.log('[ProgressService.submitQuizAnswer] START - User:', userId, 'Quiz:', quizId, 'Question:', questionId);
    
    try {
      const progress = await this.progressUseCase.submitQuizAnswer(userId, quizId, questionId, answer, timeSpentSeconds);
      
      // Find the answer for this question to determine if it was correct
      const questionAnswer = progress.answers.find(a => a.questionId === questionId);
      const isCorrect = questionAnswer?.isCorrect || false;

      console.log('[ProgressService.submitQuizAnswer] SUCCESS - Answer submitted, correct:', isCorrect);
      return { success: true, progress, isCorrect };

    } catch (error) {
      console.error('[ProgressService.submitQuizAnswer] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to submit quiz answer' 
      };
    }
  }

  /**
   * Submit a complete quiz attempt
   * Called when user finishes taking a quiz with all answers
   */
  async submitQuizAttempt(
    quizAttempt: {
      quizId: string;
      courseId: string;
      moduleId: string;
      answers: Record<string, number[]>;
      score: number;
      maxScore: number;
      percentage: number;
      passed: boolean;
      timeSpentSeconds: number;
      attemptNumber: number;
    }
  ): Promise<GetQuizProgressData> {
    console.log('[ProgressService.submitQuizAttempt] START - Quiz:', quizAttempt.quizId);
    
    if (!this.currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      // Create quiz progress record
      const progressData = {
        userId: this.currentUser.uid,
        quizId: quizAttempt.quizId,
        courseId: quizAttempt.courseId,
        moduleId: quizAttempt.moduleId,
        answers: quizAttempt.answers,
        score: quizAttempt.score,
        maxScore: quizAttempt.maxScore,
        percentage: quizAttempt.percentage,
        passed: quizAttempt.passed,
        timeSpentSeconds: quizAttempt.timeSpentSeconds,
        startedAt: new Date(),
        completedAt: new Date(),
        attemptNumber: quizAttempt.attemptNumber
      };

      const quizProgress = await this.progressUseCase.submitQuizAttempt(
        this.currentUser.uid,
        progressData
      );

      // Update module progress if quiz passed
      if (quizAttempt.passed) {
        await this.progressUseCase.updateModuleProgress(
          this.currentUser.uid,
          quizAttempt.courseId,
          quizAttempt.moduleId
        );

        // Update course progress
        await this.progressUseCase.updateCourseProgress(
          this.currentUser.uid,
          quizAttempt.courseId
        );
      }

      // Track analytics
      await this.analyticsUseCase.trackQuizSubmission({
        userId: this.currentUser.uid,
        quizId: quizAttempt.quizId,
        courseId: quizAttempt.courseId,
        moduleId: quizAttempt.moduleId,
        score: quizAttempt.score,
        maxScore: quizAttempt.maxScore,
        percentage: quizAttempt.percentage,
        passed: quizAttempt.passed,
        timeSpent: quizAttempt.timeSpentSeconds,
        attemptNumber: quizAttempt.attemptNumber,
        timestamp: new Date()
      });

      console.log('[ProgressService.submitQuizAttempt] SUCCESS - Passed:', quizAttempt.passed);
      return quizProgress;
    } catch (error) {
      console.error('[ProgressService.submitQuizAttempt] ERROR:', error);
      throw error;
    }
  }

  /**
   * Get quiz progress summary for a user
   */
  async getQuizProgressSummary(userId: string, quizId: string): Promise<{
    quizId: string;
    userId: string;
    attempts: any[];
    bestScore: number;
    bestPercentage: number;
    passed: boolean;
    totalAttempts: number;
  } | null> {
    console.log('[ProgressService.getQuizProgressSummary] START - User:', userId, 'Quiz:', quizId);
    
    try {
      // Get all quiz attempts for this user and quiz
      const attempts = await this.progressUseCase.getQuizAttempts(userId, quizId);
      
      if (!attempts || attempts.length === 0) {
        return null;
      }

      // Calculate summary statistics
      const bestAttempt = attempts.reduce((best, current) => 
        current.percentage > best.percentage ? current : best
      );

      return {
        quizId: quizId,
        userId: userId,
        attempts,
        bestScore: bestAttempt.score,
        bestPercentage: bestAttempt.percentage,
        passed: attempts.some(attempt => attempt.passed),
        totalAttempts: attempts.length
      };
    } catch (error) {
      console.error('[ProgressService.getQuizProgressSummary] ERROR:', error);
      throw error;
    }
  }

  /**
   * Complete and submit quiz
   * Called when user finishes taking a quiz
   */
  async completeQuiz(userId: string, quizId: string, answers: QuizAnswerData[], totalTimeSeconds: number): Promise<{
    success: boolean;
    progress?: GetQuizProgressData;
    moduleProgress?: GetModuleProgressData;
    courseProgress?: GetCourseProgressData;
    passed?: boolean;
    score?: number;
    error?: string;
  }> {
    console.log('[ProgressService.completeQuiz] START - User:', userId, 'Quiz:', quizId, 'Answers:', answers.length);
    
    try {
      const progress = await this.progressUseCase.completeQuiz(userId, quizId, answers, totalTimeSeconds);

      // Get updated module and course progress if quiz was passed
      let moduleProgress: GetModuleProgressData | undefined;
      let courseProgress: GetCourseProgressData | undefined;

      if (progress.passed && progress.moduleId) {
        moduleProgress = await this.progressUseCase.calculateModuleProgress(userId, progress.moduleId);
        courseProgress = await this.progressUseCase.calculateCourseProgress(userId, progress.courseId);
      }

      // Track quiz completion analytics
      if (this.analyticsUseCase) {
        try {
          await this.analyticsUseCase.trackActivity(
            userId,
            'quizCompleted',
            {
              courseId: progress.courseId,
              moduleId: progress.moduleId,
              lessonId: progress.lessonId,
              quizId: quizId,
              attemptNumber: progress.attemptNumber,
              score: progress.score,
              maxScore: progress.maxScore,
              passingScore: progress.passingScore,
              passed: progress.passed,
              timeSpentSeconds: totalTimeSeconds,
              questionsAnswered: answers.length,
              totalQuestions: progress.totalQuestions,
              progressId: progress.id
            }
          );
        } catch (analyticsError) {
          console.warn('[ProgressService.completeQuiz] Analytics tracking failed:', analyticsError);
        }
      }

      console.log('[ProgressService.completeQuiz] SUCCESS - Quiz completed, passed:', progress.passed, 'score:', progress.score);
      return { 
        success: true, 
        progress, 
        moduleProgress, 
        courseProgress,
        passed: progress.passed,
        score: progress.score 
      };

    } catch (error) {
      console.error('[ProgressService.completeQuiz] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to complete quiz' 
      };
    }
  }

  // ===== PROGRESS QUERIES =====

  /**
   * Get comprehensive progress summary for a user
   * Includes all courses, modules, lessons, and quizzes
   */
  async getUserProgressSummary(userId: string, courseId?: string): Promise<{
    success: boolean;
    data?: {
      courseProgress: GetCourseProgressData[];
      moduleProgress: GetModuleProgressData[];
      lessonProgress: GetLessonProgressData[];
      quizProgress: GetQuizProgressData[];
      overallStats: {
        totalCoursesStarted: number;
        totalCoursesCompleted: number;
        totalLessonsCompleted: number;
        totalQuizzesCompleted: number;
        averageQuizScore: number;
        totalTimeSpentHours: number;
      };
    };
    error?: string;
  }> {
    console.log('[ProgressService.getUserProgressSummary] START - User:', userId, 'Course:', courseId);
    
    try {
      const data = await this.progressUseCase.getUserProgressSummary(userId, courseId);

      console.log('[ProgressService.getUserProgressSummary] SUCCESS - Summary retrieved');
      return { success: true, data };

    } catch (error) {
      console.error('[ProgressService.getUserProgressSummary] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to get progress summary' 
      };
    }
  }

  /**
   * Get detailed learning analytics for a course
   * Provides insights into learning patterns and recommendations
   */
  async getLearningAnalytics(userId: string, courseId: string): Promise<{
    success: boolean;
    data?: {
      dailyActivity: { date: string; lessonsCompleted: number; timeSpentMinutes: number; }[];
      weakAreas: { topic: string; averageScore: number; attemptsCount: number; }[];
      strongAreas: { topic: string; averageScore: number; attemptsCount: number; }[];
      learningVelocity: { week: string; completionRate: number; }[];
      recommendedActions: string[];
    };
    error?: string;
  }> {
    console.log('[ProgressService.getLearningAnalytics] START - User:', userId, 'Course:', courseId);
    
    try {
      const data = await this.progressUseCase.getLearningAnalytics(userId, courseId);

      // Track analytics access
      if (this.analyticsUseCase) {
        try {
          await this.analyticsUseCase.trackActivity(
            userId,
            'learningAnalyticsViewed',
            {
              courseId: courseId,
              weakAreasCount: data.weakAreas.length,
              strongAreasCount: data.strongAreas.length,
              recommendationsCount: data.recommendedActions.length
            }
          );
        } catch (analyticsError) {
          console.warn('[ProgressService.getLearningAnalytics] Analytics tracking failed:', analyticsError);
        }
      }

      console.log('[ProgressService.getLearningAnalytics] SUCCESS - Analytics retrieved');
      return { success: true, data };

    } catch (error) {
      console.error('[ProgressService.getLearningAnalytics] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to get learning analytics' 
      };
    }
  }

  // ===== CONVENIENCE METHODS =====

  /**
   * Get user's progress for a specific course
   * Returns all progress data for a single course
   */
  async getCourseProgressDetails(userId: string, courseId: string): Promise<{
    success: boolean;
    data?: {
      courseProgress: GetCourseProgressData | null;
      moduleProgress: GetModuleProgressData[];
      lessonProgress: GetLessonProgressData[];
      quizProgress: GetQuizProgressData[];
      completionPercentage: number;
      timeSpentHours: number;
    };
    error?: string;
  }> {
    console.log('[ProgressService.getCourseProgressDetails] START - User:', userId, 'Course:', courseId);
    
    try {
      const summary = await this.progressUseCase.getUserProgressSummary(userId, courseId);
      
      const courseProgress = summary.courseProgress[0] || null;
      const completionPercentage = courseProgress?.completionPercentage || 0;
      const timeSpentHours = Math.round((courseProgress?.totalTimeSpentSeconds || 0) / 3600 * 100) / 100;

      const data = {
        courseProgress,
        moduleProgress: summary.moduleProgress,
        lessonProgress: summary.lessonProgress,
        quizProgress: summary.quizProgress,
        completionPercentage,
        timeSpentHours
      };

      console.log('[ProgressService.getCourseProgressDetails] SUCCESS - Course progress retrieved');
      return { success: true, data };

    } catch (error) {
      console.error('[ProgressService.getCourseProgressDetails] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to get course progress details' 
      };
    }
  }

  /**
   * Check if user has completed a specific lesson
   * Quick utility method for UI state management
   */
  async isLessonCompleted(userId: string, lessonId: string): Promise<{
    success: boolean;
    completed?: boolean;
    error?: string;
  }> {
    console.log('[ProgressService.isLessonCompleted] START - User:', userId, 'Lesson:', lessonId);
    
    try {
      const progressRepo = new ProgressRepository();
      const progress = await progressRepo.getLessonProgressByUser(userId, lessonId);
      
      const completed = progress?.status === 'completed';
      
      console.log('[ProgressService.isLessonCompleted] SUCCESS - Completed:', completed);
      return { success: true, completed };

    } catch (error) {
      console.error('[ProgressService.isLessonCompleted] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to check lesson completion' 
      };
    }
  }

  /**
   * Check if user has passed a specific quiz
   * Quick utility method for UI state management
   */
  async isQuizPassed(userId: string, quizId: string): Promise<{
    success: boolean;
    passed?: boolean;
    bestScore?: number;
    attemptsCount?: number;
    error?: string;
  }> {
    console.log('[ProgressService.isQuizPassed] START - User:', userId, 'Quiz:', quizId);
    
    try {
      const progressRepo = new ProgressRepository();
      const attempts = await progressRepo.getQuizProgressByUser(userId, quizId);
      
      const passedAttempts = attempts.filter(a => a.passed);
      const passed = passedAttempts.length > 0;
      const bestScore = attempts.length > 0 
        ? Math.max(...attempts.map(a => a.score || 0))
        : undefined;
      
      console.log('[ProgressService.isQuizPassed] SUCCESS - Passed:', passed, 'Best score:', bestScore);
      return { 
        success: true, 
        passed, 
        bestScore,
        attemptsCount: attempts.length 
      };

    } catch (error) {
      console.error('[ProgressService.isQuizPassed] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to check quiz status' 
      };
    }
  }

  // ===== ENROLLMENT INTEGRATION =====

  /**
   * Initialize progress tracking when user enrolls in a course
   * Called during the enrollment process
   */
  async initializeEnrollmentProgress(userId: string, courseId: string, enrollmentId: string): Promise<{
    success: boolean;
    courseProgress?: GetCourseProgressData;
    error?: string;
  }> {
    console.log('[ProgressService.initializeEnrollmentProgress] START - User:', userId, 'Course:', courseId, 'Enrollment:', enrollmentId);
    
    try {
      // Check if course progress already exists
      const existingProgress = await this.progressUseCase.getUserProgressSummary(userId, courseId);
      
      if (existingProgress.courseProgress.length > 0) {
        console.log('[ProgressService.initializeEnrollmentProgress] Progress already exists, updating enrollment ID');
        // Update existing progress with enrollment ID
        const progressRepo = new (await import('../repository/progress.repository')).ProgressRepository();
        const updatedProgress = await progressRepo.updateCourseProgress(existingProgress.courseProgress[0].id, {
          enrollmentId: enrollmentId
        });
        return { success: true, courseProgress: updatedProgress };
      } else {
        console.log('[ProgressService.initializeEnrollmentProgress] Creating new course progress');
        // Create initial course progress record
        const progressRepo = new (await import('../repository/progress.repository')).ProgressRepository();
        const courseProgress = await progressRepo.createCourseProgress({
          userId: userId,
          courseId: courseId,
          enrollmentId: enrollmentId,
          status: 'notStarted',
          completionPercentage: 0,
          modulesCompleted: 0,
          totalModules: 0,
          lessonsCompleted: 0,
          totalLessons: 0,
          quizzesCompleted: 0,
          totalQuizzes: 0,
          totalTimeSpentSeconds: 0,
          lastAccessedAt: new Date()
        });

        // Track enrollment progress initialization
        if (this.analyticsUseCase) {
          try {
            await this.analyticsUseCase.trackActivity(
              userId,
              'enrollmentProgressInitialized',
              {
                courseId: courseId,
                enrollmentId: enrollmentId,
                progressId: courseProgress.id
              }
            );
          } catch (analyticsError) {
            console.warn('[ProgressService.initializeEnrollmentProgress] Analytics tracking failed:', analyticsError);
          }
        }

        return { success: true, courseProgress };
      }
    } catch (error) {
      console.error('[ProgressService.initializeEnrollmentProgress] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to initialize enrollment progress' 
      };
    }
  }

  /**
   * Get progress for all enrolled students in a course (for instructors)
   * Returns detailed progress data for instructor dashboard
   */
  async getCourseStudentProgress(instructorId: string, courseId: string): Promise<{
    success: boolean;
    data?: {
      courseInfo: {
        id: string;
        title: string;
        totalModules: number;
        totalLessons: number;
        totalQuizzes: number;
      };
      studentProgress: Array<{
        userId: string;
        userName: string;
        userEmail: string;
        enrollmentDate: Date;
        lastAccessed: Date;
        overallProgress: number;
        status: 'notStarted' | 'inProgress' | 'completed';
        timeSpentHours: number;
        lessonsCompleted: number;
        quizzesCompleted: number;
        averageQuizScore: number;
        moduleProgress: Array<{
          moduleId: string;
          moduleName: string;
          progress: number;
          lessonsCompleted: number;
          totalLessons: number;
        }>;
        recentActivity: Array<{
          type: 'lesson' | 'quiz';
          itemId: string;
          itemName: string;
          completedAt: Date;
          score?: number;
        }>;
      }>;
      summary: {
        totalEnrolled: number;
        activeStudents: number;
        completedStudents: number;
        averageProgress: number;
        averageTimeSpent: number;
      };
    };
    error?: string;
  }> {
    console.log('[ProgressService.getCourseStudentProgress] START - Instructor:', instructorId, 'Course:', courseId);
    
    try {
      // First verify instructor has access to this course
      // This would typically check course ownership or instructor assignment
      
      // Get all enrollments for this course
      const courseRepo = new CourseRepository();
      const enrollments = await courseRepo.getCourseEnrollments(courseId);
      
      if (!enrollments || enrollments.length === 0) {
        return {
          success: true,
          data: {
            courseInfo: {
              id: courseId,
              title: 'Course',
              totalModules: 0,
              totalLessons: 0,
              totalQuizzes: 0
            },
            studentProgress: [],
            summary: {
              totalEnrolled: 0,
              activeStudents: 0,
              completedStudents: 0,
              averageProgress: 0,
              averageTimeSpent: 0
            }
          }
        };
      }

      // Get progress for each enrolled student
      const studentProgressPromises = enrollments.map(async (enrollment) => {
        try {
          const progressSummary = await this.progressUseCase.getUserProgressSummary(enrollment.userId, courseId);
          const courseProgress = progressSummary.courseProgress[0];
          
          // Get user details (this would come from user repository)
          const userRepo = new (await import('../repository/user.repository')).UserRepository();
          const userDetails = await userRepo.getUserById(enrollment.userId);
          
          // Calculate recent activity
          const recentActivity = [
            ...progressSummary.lessonProgress
              .filter(p => p.completedAt && p.completedAt.getTime() > Date.now() - (7 * 24 * 60 * 60 * 1000))
              .map(p => ({
                type: 'lesson' as const,
                itemId: p.lessonId,
                itemName: `Lesson ${p.lessonId}`,
                completedAt: p.completedAt!.toDate()
              })),
            ...progressSummary.quizProgress
              .filter(p => p.completedAt && p.completedAt.getTime() > Date.now() - (7 * 24 * 60 * 60 * 1000))
              .map(p => ({
                type: 'quiz' as const,
                itemId: p.quizId,
                itemName: `Quiz ${p.quizId}`,
                completedAt: p.completedAt!.toDate(),
                score: p.score
              }))
          ].sort((a, b) => b.completedAt.getTime() - a.completedAt.getTime()).slice(0, 10);

          // Calculate module progress
          const moduleProgress = progressSummary.moduleProgress.map(mp => ({
            moduleId: mp.moduleId,
            moduleName: `Module ${mp.moduleId}`,
            progress: mp.completionPercentage,
            lessonsCompleted: mp.lessonsCompleted,
            totalLessons: mp.totalLessons
          }));

          return {
            userId: enrollment.userId,
            userName: userDetails?.displayName || userDetails?.email || 'Unknown User',
            userEmail: userDetails?.email || '',
            enrollmentDate: enrollment.enrolledAt.toDate(),
            lastAccessed: courseProgress?.lastAccessedAt.toDate() || enrollment.enrolledAt.toDate(),
            overallProgress: courseProgress?.completionPercentage || 0,
            status: courseProgress?.status || 'notStarted',
            timeSpentHours: Math.round((courseProgress?.totalTimeSpentSeconds || 0) / 3600 * 100) / 100,
            lessonsCompleted: courseProgress?.lessonsCompleted || 0,
            quizzesCompleted: courseProgress?.quizzesCompleted || 0,
            averageQuizScore: courseProgress?.averageQuizScore || 0,
            moduleProgress,
            recentActivity
          };
        } catch (error) {
          console.error('[ProgressService.getCourseStudentProgress] Error processing student:', enrollment.userId, error);
          return null;
        }
      });

      type CourseStudentProgressRow = {
        userId: string;
        userName: string;
        userEmail: string;
        enrollmentDate: Date;
        lastAccessed: Date;
        overallProgress: number;
        status: string;
        timeSpentHours: number;
        lessonsCompleted: number;
        quizzesCompleted: number;
        averageQuizScore: number;
        moduleProgress: Array<{
          moduleId: string;
          moduleName: string;
          progress: number;
          lessonsCompleted: number;
          totalLessons: number;
        }>;
        recentActivity: Array<{
          type: 'lesson' | 'quiz';
          itemId: string;
          itemName: string;
          completedAt: Date;
          score?: number;
        }>;
      };

      const studentProgressResults = (await Promise.all(studentProgressPromises)).filter(
        (r): r is CourseStudentProgressRow => r != null,
      );

      // Calculate summary statistics
      const totalEnrolled = studentProgressResults.length;
      const activeStudents = studentProgressResults.filter(s => s!.lastAccessed.getTime() > Date.now() - (7 * 24 * 60 * 60 * 1000)).length;
      const completedStudents = studentProgressResults.filter(s => s!.status === 'completed').length;
      const averageProgress = totalEnrolled > 0 
        ? Math.round(studentProgressResults.reduce((sum, s) => sum + s!.overallProgress, 0) / totalEnrolled)
        : 0;
      const averageTimeSpent = totalEnrolled > 0
        ? Math.round(studentProgressResults.reduce((sum, s) => sum + s!.timeSpentHours, 0) / totalEnrolled * 100) / 100
        : 0;

      // Get course info
      const course = await courseRepo.getCourseById(courseId);

      console.log('[ProgressService.getCourseStudentProgress] SUCCESS - Students:', totalEnrolled);
      return {
        success: true,
        data: {
          courseInfo: {
            id: courseId,
            title: course?.title || 'Course',
            totalModules: studentProgressResults[0]?.moduleProgress.length || 0,
            totalLessons: Math.max(...studentProgressResults.map(s => s!.moduleProgress.reduce((sum, m) => sum + m.totalLessons, 0)), 0),
            totalQuizzes: 0 // This would need to be calculated from course structure
          },
          studentProgress: studentProgressResults,
          summary: {
            totalEnrolled,
            activeStudents,
            completedStudents,
            averageProgress,
            averageTimeSpent
          }
        }
      };

    } catch (error) {
      console.error('[ProgressService.getCourseStudentProgress] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to get course student progress' 
      };
    }
  }

  /**
   * Get detailed quiz performance report for instructors
   * Shows who answered what questions and their performance
   */
  async getQuizPerformanceReport(instructorId: string, quizId: string): Promise<{
    success: boolean;
    data?: {
      quizInfo: {
        id: string;
        title: string;
        courseId: string;
        courseName: string;
        totalQuestions: number;
        passingScore: number;
        timeLimit: number;
      };
      attempts: Array<{
        userId: string;
        userName: string;
        userEmail: string;
        attemptNumber: number;
        startedAt: Date;
        completedAt?: Date;
        score: number;
        maxScore: number;
        passed: boolean;
        timeSpentMinutes: number;
        answers: Array<{
          questionId: string;
          questionText: string;
          userAnswer: string | string[];
          correctAnswer: string | string[];
          isCorrect: boolean;
          pointsEarned: number;
          maxPoints: number;
          timeSpentSeconds: number;
        }>;
      }>;
      analytics: {
        totalAttempts: number;
        uniqueStudents: number;
        passRate: number;
        averageScore: number;
        averageTimeSpent: number;
        questionAnalytics: Array<{
          questionId: string;
          questionText: string;
          correctAnswers: number;
          totalAnswers: number;
          accuracyRate: number;
          averageTimeSpent: number;
          commonWrongAnswers: Array<{
            answer: string;
            count: number;
          }>;
        }>;
      };
    };
    error?: string;
  }> {
    console.log('[ProgressService.getQuizPerformanceReport] START - Instructor:', instructorId, 'Quiz:', quizId);
    
    try {
      // Get all quiz attempts
      const progressRepo = new (await import('../repository/progress.repository')).ProgressRepository();
      const userRepo = new (await import('../repository/user.repository')).UserRepository();
      const quizRepo = new QuizRepository();
      
      // Get quiz details
      const quiz = await quizRepo.getQuizById(quizId);
      if (!quiz) {
        throw new Error('Quiz not found');
      }

      // Get all attempts for this quiz
      const allQuizProgress = await progressRepo.getUserQuizProgress('', quiz.courseId);
      const quizAttempts = allQuizProgress.filter(p => p.quizId === quizId);

      if (quizAttempts.length === 0) {
        return {
          success: true,
          data: {
            quizInfo: {
              id: quizId,
              title: quiz.title,
              courseId: quiz.courseId,
              courseName: 'Course',
              totalQuestions: quiz.questionsCount || 0,
              passingScore: quiz.passingScore,
              timeLimit: quiz.timeLimitMinutes || 0
            },
            attempts: [],
            analytics: {
              totalAttempts: 0,
              uniqueStudents: 0,
              passRate: 0,
              averageScore: 0,
              averageTimeSpent: 0,
              questionAnalytics: []
            }
          }
        };
      }

      // Process attempts with user details
      const attemptsWithDetails = await Promise.all(
        quizAttempts.map(async (attempt) => {
          const userDetails = await userRepo.getUserById(attempt.userId);
          return {
            userId: attempt.userId,
            userName: userDetails?.displayName || userDetails?.email || 'Unknown User',
            userEmail: userDetails?.email || '',
            attemptNumber: attempt.attemptNumber,
            startedAt: attempt.startedAt.toDate(),
            completedAt: attempt.completedAt?.toDate(),
            score: attempt.score || 0,
            maxScore: attempt.maxScore,
            passed: attempt.passed,
            timeSpentMinutes: Math.round(attempt.timeSpentSeconds / 60),
            answers: attempt.answers
          };
        })
      );

      // Calculate analytics
      const totalAttempts = quizAttempts.length;
      const uniqueStudents = new Set(quizAttempts.map(a => a.userId)).size;
      const passedAttempts = quizAttempts.filter(a => a.passed).length;
      const passRate = totalAttempts > 0 ? Math.round((passedAttempts / totalAttempts) * 100) : 0;
      const averageScore = totalAttempts > 0 
        ? Math.round(quizAttempts.reduce((sum, a) => sum + (a.score || 0), 0) / totalAttempts)
        : 0;
      const averageTimeSpent = totalAttempts > 0
        ? Math.round(quizAttempts.reduce((sum, a) => sum + a.timeSpentSeconds, 0) / totalAttempts / 60)
        : 0;

      // Analyze questions
      const questionAnalytics = new Map<string, {
        questionId: string;
        questionText: string;
        correctAnswers: number;
        totalAnswers: number;
        totalTimeSpent: number;
        wrongAnswers: Map<string, number>;
      }>();

      quizAttempts.forEach(attempt => {
        attempt.answers.forEach(answer => {
          if (!questionAnalytics.has(answer.questionId)) {
            questionAnalytics.set(answer.questionId, {
              questionId: answer.questionId,
              questionText: answer.questionText,
              correctAnswers: 0,
              totalAnswers: 0,
              totalTimeSpent: 0,
              wrongAnswers: new Map()
            });
          }

          const qAnalytics = questionAnalytics.get(answer.questionId)!;
          qAnalytics.totalAnswers++;
          qAnalytics.totalTimeSpent += answer.timeSpentSeconds;
          
          if (answer.isCorrect) {
            qAnalytics.correctAnswers++;
          } else {
            const wrongAnswer = Array.isArray(answer.userAnswer) 
              ? answer.userAnswer.join(', ')
              : String(answer.userAnswer);
            qAnalytics.wrongAnswers.set(wrongAnswer, (qAnalytics.wrongAnswers.get(wrongAnswer) || 0) + 1);
          }
        });
      });

      const questionAnalyticsArray = Array.from(questionAnalytics.values()).map(q => ({
        questionId: q.questionId,
        questionText: q.questionText,
        correctAnswers: q.correctAnswers,
        totalAnswers: q.totalAnswers,
        accuracyRate: q.totalAnswers > 0 ? Math.round((q.correctAnswers / q.totalAnswers) * 100) : 0,
        averageTimeSpent: q.totalAnswers > 0 ? Math.round(q.totalTimeSpent / q.totalAnswers) : 0,
        commonWrongAnswers: Array.from(q.wrongAnswers.entries())
          .map(([answer, count]) => ({ answer, count }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 3)
      }));

      console.log('[ProgressService.getQuizPerformanceReport] SUCCESS - Attempts:', totalAttempts);
      return {
        success: true,
        data: {
          quizInfo: {
            id: quizId,
            title: quiz.title,
            courseId: quiz.courseId,
            courseName: 'Course', // This would come from course lookup
            totalQuestions: quiz.questionsCount || questionAnalyticsArray.length,
            passingScore: quiz.passingScore,
            timeLimit: quiz.timeLimitMinutes || 0
          },
          attempts: attemptsWithDetails,
          analytics: {
            totalAttempts,
            uniqueStudents,
            passRate,
            averageScore,
            averageTimeSpent,
            questionAnalytics: questionAnalyticsArray
          }
        }
      };

    } catch (error) {
      console.error('[ProgressService.getQuizPerformanceReport] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to get quiz performance report' 
      };
    }
  }

  // ===== BULK OPERATIONS =====

  /**
   * Recalculate all progress for a user in a course
   * Useful for data migration or fixing inconsistencies
   */
  async recalculateCourseProgress(userId: string, courseId: string): Promise<{
    success: boolean;
    courseProgress?: GetCourseProgressData;
    moduleProgress?: GetModuleProgressData[];
    error?: string;
  }> {
    console.log('[ProgressService.recalculateCourseProgress] START - User:', userId, 'Course:', courseId);
    
    try {
      // Get all module IDs for this course (this would need to be implemented)
      // For now, we'll recalculate based on existing progress
      const summary = await this.progressUseCase.getUserProgressSummary(userId, courseId);
      const moduleIds = [...new Set(summary.moduleProgress.map(m => m.moduleId))];

      // Recalculate each module progress
      const updatedModuleProgress: GetModuleProgressData[] = [];
      for (const moduleId of moduleIds) {
        const moduleProgress = await this.progressUseCase.calculateModuleProgress(userId, moduleId);
        updatedModuleProgress.push(moduleProgress);
      }

      // Recalculate course progress
      const courseProgress = await this.progressUseCase.calculateCourseProgress(userId, courseId);

      console.log('[ProgressService.recalculateCourseProgress] SUCCESS - Progress recalculated');
      return { 
        success: true, 
        courseProgress, 
        moduleProgress: updatedModuleProgress 
      };

    } catch (error) {
      console.error('[ProgressService.recalculateCourseProgress] ERROR:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Failed to recalculate progress' 
      };
    }
  }
}
